import { useFetchData } from "../../../hooks/useFetch";
import {
  ApiConfig,
  API_ORDER_REPORT_ROUTER,
  API_CUSTOMER_ITEM_DEBT_ROUTER,
} from "../../../services/api-config";

// ===== Loại bút toán sổ đồ (khớp BE `ITEM_DEBT_ENTRY_TYPE`) =====
export enum ITEM_DEBT_ENTRY_TYPE {
  OWE = 1,
  RETURN = 2,
  ADJUST = 3,
  LOST = 4,
  NON_STANDARD = 5,
  OVER_DELIVERED = 6,
}
export const ITEM_DEBT_ENTRY_LABEL: Record<number, string> = {
  [ITEM_DEBT_ENTRY_TYPE.OWE]: "Nợ khách",
  [ITEM_DEBT_ENTRY_TYPE.RETURN]: "Trả nợ",
  [ITEM_DEBT_ENTRY_TYPE.ADJUST]: "Điều chỉnh",
  [ITEM_DEBT_ENTRY_TYPE.LOST]: "Thất thoát",
  [ITEM_DEBT_ENTRY_TYPE.NON_STANDARD]: "Hàng không chuẩn",
  [ITEM_DEBT_ENTRY_TYPE.OVER_DELIVERED]: "Giao dư",
};

export interface ReconciliationRow {
  category: string;
  category_id: number | null;
  dirty: Record<string, number>;
  dirty_total: number;
  /** Đã GỒM phần trả nợ (xem `clean_returned`). */
  clean: Record<string, number>;
  clean_total: number;
  /** ngày → phần trả nợ nằm trong `clean` (đồ trả nợ nhập ở box "Trả nợ", ngoài số kiểm đếm giao). */
  clean_returned?: Record<string, number>;
  clean_returned_total?: number;
  /**
   * ngày → NỢ KHÁCH ghi lúc kiểm đếm giao của đơn hoàn thành ngày đó. Đồ CHƯA giao → KHÔNG nằm trong `clean`
   * (ngược với `clean_returned`). Đã trừ phần nợ được gỡ khi duyệt điều chỉnh.
   */
  clean_owed?: Record<string, number>;
  clean_owed_total?: number;
  opening_debt: number;
  closing_debt: number;
  diff_in_period: number;
  wip_open: number;
  wip_close: number;
  breakdown: {
    owe: number;
    returned: number;
    lost: number;
    non_standard: number;
    adjust: number;
  };
  balance_gap: number;
}

export interface ReconciliationReport {
  customer_id: number;
  from_date: string;
  to_date: string;
  days: string[];
  categories: ReconciliationRow[];
  weight_by_date: Record<string, number>;
  weight_total: number;
  order_count: number;
  totals: {
    dirty: number;
    clean: number;
    clean_returned?: number;
    clean_owed?: number;
    opening_debt: number;
    closing_debt: number;
    wip_open: number;
    wip_close: number;
    owe: number;
    returned: number;
    lost: number;
    non_standard: number;
    adjust: number;
  };
  is_balanced: boolean;
}

export interface ItemDebtLog {
  id: number;
  customer_id: number;
  branch_id: number;
  category: string;
  category_id: number | null;
  quantity: number;
  entry_type: number;
  order_id: number;
  /** Mã đơn (OD653) — BE enrich sẵn; null nếu bút toán không gắn đơn (điều chỉnh tay). */
  order_code: string | null;
  stage: string | null;
  fault_ticket_id: number | null;
  note: string | null;
  created_by: number;
  created_at: string;
}

export interface CustomerQty {
  customer_id: number;
  customer_name: string;
  quantity: number;
}

export interface CustomerPeriodRow {
  customer_id: number;
  customer_name: string;
  owe: number;
  returned: number;
  net_debt: number;
  lost: number;
  non_standard: number;
  adjust: number;
  total_diff: number;
}

export interface ItemDebtOverview {
  from_date: string;
  to_date: string;
  limit: number;
  /** Số dư nợ tới HIỆN TẠI (không theo kỳ) */
  top_debt: CustomerQty[];
  /** Hàng không chuẩn lũy kế tới hiện tại */
  top_non_standard: CustomerQty[];
  /** Chỉ khách CÓ phát sinh trong kỳ */
  period_rows: CustomerPeriodRow[];
  totals: {
    debt_now: number;
    non_standard_all_time: number;
    period_owe: number;
    period_returned: number;
    period_lost: number;
    period_non_standard: number;
    period_customers: number;
  };
}

const ReconciliationService = {
  /** Báo cáo đối soát đơn hàng (tổng hợp toàn hệ thống) */
  overview: () => {
    const { request, baseResponse, loading } = useFetchData.Get<ItemDebtOverview>({
      projectId: ApiConfig.PROJECT_ID.ORDER_SERVICE,
    });
    return {
      fetch: (params: { from_date: string; to_date: string; limit?: number }) =>
        request({ endPoint: API_ORDER_REPORT_ROUTER.API_ITEM_DEBT_OVERVIEW, data: params }),
      response: baseResponse,
      loading,
    };
  },

  /** Báo cáo đối soát khối lượng theo khách hàng (chỉ đơn kiểm đếm) */
  report: () => {
    const { request, baseResponse, loading } = useFetchData.Get<ReconciliationReport>({
      projectId: ApiConfig.PROJECT_ID.ORDER_SERVICE,
    });
    return {
      fetch: (params: { customer_id: number; from_date: string; to_date: string }) =>
        request({ endPoint: API_ORDER_REPORT_ROUTER.API_CUSTOMER_RECONCILIATION, data: params }),
      response: baseResponse,
      loading,
    };
  },

  /** Bút toán sổ đồ — truy vết nợ ở đơn nào, trả ở đơn nào */
  debtLogs: () => {
    const { request, baseResponse, loading } = useFetchData.Get<ItemDebtLog[]>({
      projectId: ApiConfig.PROJECT_ID.ORDER_SERVICE,
    });
    return {
      fetch: (params: { customer_id: number; from_date?: string; to_date?: string; limit?: number }) =>
        request({ endPoint: API_CUSTOMER_ITEM_DEBT_ROUTER.API_LOGS, data: params }),
      response: baseResponse,
      loading,
    };
  },

  /** Giám sát điều chỉnh sổ nợ (bắt buộc ghi chú lý do) */
  adjust: () => {
    const { request, baseResponse, loading } = useFetchData.Post<any>({
      projectId: ApiConfig.PROJECT_ID.ORDER_SERVICE,
    });
    return {
      fetch: (data: {
        customer_id: number;
        branch_id?: number;
        category: string;
        quantity: number;
        note: string;
      }) => request({ endPoint: API_CUSTOMER_ITEM_DEBT_ROUTER.API_ADJUST, data }),
      response: baseResponse,
      loading,
    };
  },
};

export default ReconciliationService;
